/**
 * Browser half of the named-destination mechanism.
 *
 * The edge rendered `[Try Ember](ember:try)` into a real, crawlable link:
 *
 *     <a href="/talk" data-ember-cta="try" data-log="use_case_cta_try">
 *
 * That href works with no JavaScript, which is the point — a crawler and a
 * visitor with a blocked bundle both get a working link. What it lacks is the
 * attribution query the live CTAs carry, because those params are computed from
 * this visitor's storage and URL and the server has neither.
 *
 * So this module upgrades the href in place after mount: same destination, now
 * carrying first-touch attribution.
 *
 * Why upgrade rather than render the link in React: the body of a use-case page
 * is HTML produced by the markdown renderer and injected wholesale. There are
 * no React elements in there to hand props to.
 *
 * The name table is declared here AND in src/edge/content/cta-targets.js — one
 * is TypeScript compiled by Vite, the other plain Node ESM, and neither can
 * import the other without dragging a build step across the boundary. A test
 * pins them together; a divergence would leave a button pointing at a stale
 * path with no error anywhere.
 */

import {
  collectHref,
  signInHref,
  talkHref,
} from '../marketing/src/lib/collectCta';

/** Must match CTA_TARGETS in src/edge/content/cta-targets.js. */
export const CTA_TARGET_NAMES = [
  'try', 'join', 'signin', 'home', 'stories',
  'use-cases', 'how-it-works', 'about', 'privacy', 'terms', 'free-trial',
] as const;

export type CtaTargetName = (typeof CTA_TARGET_NAMES)[number];

/**
 * How each name resolves in the browser.
 *
 * Only the three product actions have a resolver — they are the ones whose URL
 * carries attribution. The plain content paths (about, privacy, …) are already
 * correct as the edge rendered them and are deliberately absent: rewriting a
 * static path would be churn with no benefit.
 */
const RESOLVERS: Partial<Record<CtaTargetName, () => string>> = {
  try: talkHref,
  join: collectHref,
  signin: signInHref,
};

function isTargetName(value: string): value is CtaTargetName {
  return (CTA_TARGET_NAMES as readonly string[]).includes(value);
}

/**
 * Upgrade every `data-ember-cta` link inside `root`.
 *
 * Idempotent: re-running rewrites the same hrefs, so it is safe to call on
 * every render rather than only on mount. The `join` / `signin` targets are
 * plain navigations to /auth — the one door — since 2026-09-06; the marketing
 * bundle's in-place dialog that used to intercept them is gone.
 */
export function hydrateCtaLinks(root: ParentNode | null | undefined): void {
  if (!root || typeof window === 'undefined') return;
  const links = root.querySelectorAll<HTMLAnchorElement>('a[data-ember-cta]');
  links.forEach((link) => {
    const name = link.dataset.emberCta || '';
    if (!isTargetName(name)) return;

    // `try` no longer reads the guest-trial switch (2026-09-06): it goes to
    // /talk, which is open whether or not the anonymous trial is, and decides
    // for itself what the conversation needs. Nothing here can be closed
    // underneath the reader any more.
    const resolve = RESOLVERS[name];
    if (resolve) link.setAttribute('href', resolve());

  });
}
